'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '../lib/supabase/client';

export type UserRole = 'master' | 'owner' | 'admin' | 'member' | null;
export type UserPlan = 'basic' | 'premium' | 'advanced' | null;

export interface UserPermissions {
  role: UserRole;
  plan: UserPlan;
  companyId: string | null;
  isMaster: boolean;
  canAccess: (feature: string) => boolean;
}

export interface AuthCompany {
  id: string;
  name: string;
  role: Exclude<UserRole, 'master' | null>;
  plan: Exclude<UserPlan, null>;
}

// Feature → minimum plan required
const PLAN_FEATURES: Record<string, UserPlan[]> = {
  'nueva-proyecto-ia': ['basic', 'premium', 'advanced'],
  'mis-proyectos': ['basic', 'premium', 'advanced'],
  'vision-ia': ['basic', 'premium', 'advanced'],
  'crm-360': ['premium', 'advanced'],
  'emma-voice': ['advanced'],
  'admin-vision': ['basic', 'premium', 'advanced'],
  'configuracion': ['basic', 'premium', 'advanced'],
  'admin-master': [], // master only — handled separately
};

function buildPermissions(
  role: UserRole,
  plan: UserPlan,
  companyId: string | null
): UserPermissions {
  const isMaster = role === 'master';

  const canAccess = (feature: string): boolean => {
    if (isMaster) return true;
    if (feature === 'admin-master') return false;
    const allowed = PLAN_FEATURES[feature];
    if (!allowed) return true; // unknown features default open
    return plan !== null && allowed.includes(plan);
  };

  return { role, plan, companyId, isMaster, canAccess };
}

interface AuthContextValue {
  user: any;
  session: any;
  loading: boolean;
  permissions: UserPermissions;
  companies: AuthCompany[];
  activeCompany: AuthCompany | null;
  setActiveCompany: (companyId: string) => Promise<void>;
  refreshCompanies: () => Promise<void>;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signInWithGoogle: () => Promise<void>;
  signInWithFacebook: () => Promise<void>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<any>;
}

const defaultPermissions: UserPermissions = buildPermissions(null, null, null);

const AuthContext = createContext<AuthContextValue>({
  user: null,
  session: null,
  loading: true,
  permissions: defaultPermissions,
  companies: [],
  activeCompany: null,
  setActiveCompany: async () => {},
  refreshCompanies: async () => {},
  signUp: async () => {},
  signIn: async () => {},
  signInWithGoogle: async () => {},
  signInWithFacebook: async () => {},
  signOut: async () => {},
  getCurrentUser: async () => null,
  isEmailVerified: () => false,
  getUserProfile: async () => null,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState<UserPermissions>(defaultPermissions);
  const [companies, setCompanies] = useState<AuthCompany[]>([]);
  const [activeCompany, setActiveCompanyState] = useState<AuthCompany | null>(null);
  const supabase = createClient();

  const storageKeyForUser = (userId: string) => `av_active_company_${userId}`;

  async function loadPermissions(userId: string | null) {
    if (!userId) {
      setCompanies([]);
      setActiveCompanyState(null);
      setPermissions(buildPermissions(null, null, null));
      return;
    }

    const MAX_RETRIES = 2;
    let lastError: unknown;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const { data: masterRow, error: masterError } = await supabase
          .from('platform_admins')
          .select('role')
          .eq('user_id', userId)
          .maybeSingle();
        if (masterError) throw masterError;

        const isMaster = masterRow?.role === 'master';

        const { data: membershipRows, error: membershipError } = await supabase
          .from('av_memberships')
          .select('company_id, role, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: true });
        if (membershipError) throw membershipError;

        const memberships = (membershipRows ?? []) as Array<{
          company_id: string;
          role: Exclude<UserRole, 'master' | null>;
          created_at: string;
        }>;

        const companyIds = memberships.map((row) => row.company_id);

        let companyRows: Array<{ id: string; name: string }> = [];
        let subscriptionRows: Array<{ company_id: string; plan: UserPlan; trial_ends_at: string | null }> = [];

        if (companyIds.length > 0) {
          const [companiesRes, subscriptionsRes] = await Promise.all([
            supabase
              .from('av_companies')
              .select('id, name')
              .in('id', companyIds),
            supabase
              .from('platform_company_subscriptions')
              .select('company_id, plan, trial_ends_at')
              .in('company_id', companyIds),
          ]);

          if (companiesRes.error) throw companiesRes.error;
          if (subscriptionsRes.error) throw subscriptionsRes.error;

          companyRows = (companiesRes.data ?? []) as Array<{ id: string; name: string }>;
          subscriptionRows = (subscriptionsRes.data ?? []) as Array<{
            company_id: string;
            plan: UserPlan;
            trial_ends_at: string | null;
          }>;
        }

        const companyNameById = new Map(companyRows.map((row) => [row.id, row.name]));
        const subscriptionByCompany = new Map(subscriptionRows.map((row) => [row.company_id, row]));

        const resolvedCompanies: AuthCompany[] = memberships.map((membership) => {
          const subscription = subscriptionByCompany.get(membership.company_id);
          const trialIsActive =
            Boolean(subscription?.trial_ends_at) &&
            new Date(subscription!.trial_ends_at as string).getTime() > Date.now();

          const plan: Exclude<UserPlan, null> = isMaster
            ? 'advanced'
            : trialIsActive
              ? 'premium'
              : ((subscription?.plan as Exclude<UserPlan, null> | null) ?? 'basic');

          return {
            id: membership.company_id,
            name: companyNameById.get(membership.company_id) ?? 'Empresa',
            role: membership.role,
            plan,
          };
        });

        setCompanies(resolvedCompanies);

        let selectedCompany: AuthCompany | null = null;
        if (resolvedCompanies.length > 0) {
          const savedCompanyId =
            typeof window !== 'undefined'
              ? window.localStorage.getItem(storageKeyForUser(userId))
              : null;

          selectedCompany =
            resolvedCompanies.find((company) => company.id === savedCompanyId) ??
            resolvedCompanies[0];

          if (typeof window !== 'undefined') {
            window.localStorage.setItem(storageKeyForUser(userId), selectedCompany.id);
          }
        }

        setActiveCompanyState(selectedCompany);

        if (isMaster) {
          setPermissions(
            buildPermissions('master', 'advanced', selectedCompany?.id ?? null)
          );
        } else {
          setPermissions(
            buildPermissions(
              selectedCompany?.role ?? null,
              selectedCompany?.plan ?? null,
              selectedCompany?.id ?? null
            )
          );
        }

        return;
      } catch (err) {
        lastError = err;
        if (attempt < MAX_RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, 300 * (attempt + 1)));
        }
      }
    }

    console.warn('[AuthContext] loadPermissions failed after retries:', lastError);
    setCompanies([]);
    setActiveCompanyState(null);
    setPermissions(buildPermissions(null, null, null));
  }

  const refreshCompanies = async () => {
    await loadPermissions(user?.id ?? null);
  };

  const setActiveCompany = async (companyId: string) => {
    if (!user?.id) return;

    const nextCompany = companies.find((company) => company.id === companyId);
    if (!nextCompany) {
      throw new Error('No tienes acceso a esa empresa.');
    }

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(storageKeyForUser(user.id), nextCompany.id);
    }

    setActiveCompanyState(nextCompany);
    setPermissions(
      buildPermissions(
        permissions.isMaster ? 'master' : nextCompany.role,
        permissions.isMaster ? 'advanced' : nextCompany.plan,
        nextCompany.id
      )
    );
  }

  useEffect(() => {
    // ── Orchestration flags ────────────────────────────────────────────────
    // `initDone`        – true once getSession has finished (including loadPermissions).
    // `loadInFlight`    – true while any loadPermissions call is executing.
    //                     Acts as a mutex: onAuthStateChange will not start a new
    //                     load while one is already running.
    // `lastUserId`      – the userId whose permissions were last successfully loaded.
    //                     Prevents redundant reloads for the same session.
    let initDone = false;
    let loadInFlight = false;
    let lastUserId: string | null | undefined = undefined;

    // ── Initial load: getSession is the single entry point ─────────────────
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      const userId = session?.user?.id ?? null;
      lastUserId = userId;
      loadInFlight = true;
      try {
        await loadPermissions(userId);
      } finally {
        loadInFlight = false;
        initDone = true;       // mark AFTER load completes, not before
        setLoading(false);
      }
    });

    // ── Subsequent auth changes only ───────────────────────────────────────
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const userId = session?.user?.id ?? null;

      // 1. Initialization not yet complete — getSession owns this load, skip.
      if (!initDone) return;

      // 2. A load is already in flight — do not start a concurrent one.
      if (loadInFlight) return;

      // 3. Same user as already loaded — permissions are current, skip.
      if (userId === lastUserId) return;

      // 4. Genuine session change (new login or sign-out) — reload permissions.
      lastUserId = userId;
      setSession(session);
      setUser(session?.user ?? null);
      loadInFlight = true;
      loadPermissions(userId).finally(() => {
        loadInFlight = false;
        setLoading(false);
      });
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || ''
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/login`,
      },
    });
    if (error) throw error;
  };

  const signInWithFacebook = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'facebook',
      options: {
        redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback`,
      },
    });
    if (error) throw error;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => user?.email_confirmed_at !== null;

  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value: AuthContextValue = {
    user,
    session,
    loading,
    permissions,
    companies,
    activeCompany,
    setActiveCompany,
    refreshCompanies,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithFacebook,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
